import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdCheckCircle, MdErrorOutline } from "react-icons/md";
import { useIncompleteProfileTasksQuery } from "src/lib/endpoints/profile/profile.api";
import LinkButton from "../Button/LinkButton";
import Heading from "../EditProfile/Heading";
import { ScrollArea } from "../ui/scroll-area";
import ModalContainer from "./ModalContainer";

type ProfileTask = {
  key: string;
  label: string;
  /** true when the app must block until it is filled; decided by the API's getSingleTalentIncompleteProfile. */
  required: boolean;
};

/**
 * Pages the blocker steps aside on.
 *
 * /profile because it is the only way out — blocking it would leave the
 * talent with a modal and no means of satisfying it.
 *
 * /support because a talent who cannot finish their profile is exactly the
 * talent who needs to reach us, and a blocker over the help desk turns a
 * question we could have answered into someone giving up. Reported from
 * production by talents who hit it.
 */
const OPEN_PATHS = ["/profile", "/support"];

/**
 * Blocks the app until the profile has everything matching needs.
 *
 * What counts as "needed" is decided by the API (role, level, specialty,
 * tools, skills, city/state/country, photo and CV), so this stays in step
 * with the funnel without a second checklist here. The modal is mounted globally in App.tsx
 * and cannot be dismissed; its only exit is the profile page, where it steps
 * aside so the user can actually fill things in.
 */
const CompleteProfileUpdate = () => {
  const { pathname } = useLocation();
  const { tasks, isLoading, getTasks } = useIncompleteProfileTasksQuery();

  // Re-check when the user navigates - typically leaving the profile page
  // after saving - so the modal lifts as soon as the gaps are filled.
  useEffect(() => {
    getTasks();
  }, [pathname, getTasks]);

  const onOpenPage = OPEN_PATHS.some((p) => pathname.startsWith(p));
  const list = (tasks as ProfileTask[]) ?? [];
  const blocking = list.filter((t) => t.required);
  const nudges = list.filter((t) => !t.required && t.key !== "email");

  if (onOpenPage || isLoading || blocking.length === 0) return null;

  return (
    <div className="w-full">
      <ModalContainer open canClose={false}>
        <ScrollArea className="p-3 w-[90vw] max-h-[70vh] md:w-[32rem]">
          <div className="flex flex-col space-y-4 w-full">
            <Heading title="Complete your profile to get matched" />

            <p className="text-sm text-gray-600">
              Jobs are matched on your role, level, specialty, tools, skills
              and location, and clients see your photo and CV on every match.
              Until these are filled in, you won't appear in any client's
              matches.
            </p>

            <section className="flex flex-col space-y-2">
              <p className="text-sm font-semibold text-gray-800">
                Required
              </p>
              <ul className="flex flex-col space-y-1.5">
                {blocking.map((task) => (
                  <li
                    key={task.key}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <MdErrorOutline className="mt-0.5 size-4 shrink-0 text-red-500" />
                    <span>{task.label}</span>
                  </li>
                ))}
              </ul>
            </section>

            {nudges.length > 0 && (
              <section className="flex flex-col space-y-2">
                <p className="text-sm font-semibold text-gray-800">
                  Also recommended
                </p>
                <ul className="flex flex-col space-y-1.5">
                  {nudges.map((task) => (
                    <li
                      key={task.key}
                      className="flex items-start gap-2 text-sm text-gray-500"
                    >
                      <MdCheckCircle className="mt-0.5 size-4 shrink-0 text-gray-300" />
                      <span>{task.label}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* The modal covers the whole viewport, nav included, so
                exempting /support from the blocker is no use on its own —
                there would be no way to reach it. This is the way out for a
                talent who cannot finish their profile and needs to tell
                someone why. */}
            <div className="w-full flex flex-wrap items-center justify-between gap-3 pt-2">
              <Link
                to="/support"
                className="text-sm text-gray-500 underline underline-offset-4 hover:text-gray-700"
              >
                Stuck? Contact support
              </Link>
              <LinkButton
                href="profile"
                label="Complete profile"
                className="text-white bg-primary"
                icon="arrow-upright"
              />
            </div>
          </div>
        </ScrollArea>
      </ModalContainer>
    </div>
  );
};

export default CompleteProfileUpdate;
